import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/common";
import { Delete, ArrowLeft } from "lucide-react";

const CHARS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z"];
const DEMO = [
  { r: "Admin ASD", e: "admin@olimpia.it", p: "OLIMPIA1" },
  { r: "Segreteria", e: "segreteria@olimpia.it", p: "SEGRE123" },
  { r: "Tesoriere", e: "tesoriere@olimpia.it", p: "TESOR123" },
  { r: "Istruttore", e: "istruttore@olimpia.it", p: "ISTRU123" },
  { r: "Atleta", e: "atleta@olimpia.it", p: "ATLETA12" },
  { r: "Genitore", e: "genitore@olimpia.it", p: "GENIT123" },
];

export default function Login() {
  const { login, loginGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (em = email, pn = pin) => {
    if (!em || pn.length !== 8) { toast.error("Inserisci email e PIN di 8 caratteri"); return; }
    setBusy(true);
    try {
      await login(em, pn);
      navigate("/app");
    } catch (e) {
      toast.error(apiErr(e));
      setPin("");
    } finally { setBusy(false); }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const addChar = (c) => setPin((p) => (p.length < 8 ? p + c : p));

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col lg:flex-row">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-2.5 text-white relative z-10">
          <div className="w-9 h-9 rounded-xl bg-sky-500 grid place-items-center font-bold font-display">A</div>
          <span className="font-bold font-display text-lg">ASD Manager Cloud</span>
        </Link>
        <div className="relative z-10">
          <h2 className="text-3xl font-bold font-display text-white leading-tight">Il gestionale semplice<br />per ASD e SSD.</h2>
          <p className="text-slate-400 mt-3 max-w-sm">Soci, tesserati, certificati, presenze, quote e contabilità. Tutto in un'unica piattaforma sicura.</p>
        </div>
        <img src="https://images.unsplash.com/photo-1778426643374-2b86e718bae0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <span className="text-slate-500 text-xs relative z-10">© 2026 ASD Manager Cloud</span>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-1.5 text-sm text-slate-500 mb-6"><ArrowLeft size={16} /> Home</Link>
          <h1 className="text-2xl font-bold font-display text-slate-900">Accedi con PIN</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Email + PIN a 8 caratteri alfanumerici.</p>

          <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="La tua email"
            className="w-full px-3.5 py-3 rounded-lg border border-slate-300 bg-white text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-sky-500" />

          <div className="flex justify-center gap-2 mb-4" data-testid="pin-display">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={`w-9 h-11 rounded-lg border-2 grid place-items-center font-mono font-bold text-lg pin-dot ${pin[i] ? "border-sky-500 bg-sky-50 text-slate-900" : "border-slate-200 bg-slate-50"}`}>
                {pin[i] || ""}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-6 sm:grid-cols-9 gap-1.5 mb-3">
            {CHARS.map((c) => (
              <button key={c} data-testid={`pin-key-${c}`} onClick={() => addChar(c)}
                className="aspect-square sm:aspect-auto sm:py-2 rounded-lg bg-white border border-slate-200 text-sm font-mono font-semibold text-slate-700 hover:bg-slate-100 active:scale-95 transition">
                {c}
              </button>
            ))}
            <button data-testid="pin-key-del" onClick={() => setPin((p) => p.slice(0, -1))}
              className="col-span-2 rounded-lg bg-slate-100 border border-slate-200 grid place-items-center text-slate-600 hover:bg-slate-200">
              <Delete size={16} />
            </button>
          </div>

          <Button data-testid="login-submit-button" onClick={() => submit()} disabled={busy} className="w-full" size="lg">
            {busy ? "Accesso..." : "Entra"}
          </Button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-slate-200" /><span className="text-xs text-slate-400">oppure</span><div className="flex-1 h-px bg-slate-200" />
          </div>
          <Button data-testid="google-login-btn" variant="outline" onClick={google} className="w-full">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4" /> Accedi con Google
          </Button>

          <p className="text-center text-sm text-slate-500 mt-6">
            Non hai un account? <Link to="/" className="text-sky-600 font-semibold hover:underline">Registra la tua ASD</Link>
          </p>

          <div className="mt-8 p-4 rounded-xl bg-slate-50 border border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Demo rapida — provala subito</p>
            <div className="flex flex-wrap gap-1.5">
              {DEMO.map((d) => (
                <button key={d.e} data-testid={`demo-login-${d.r.toLowerCase().replace(" ", "-")}`} onClick={() => { setEmail(d.e); setPin(d.p); submit(d.e, d.p); }}
                  className="text-xs px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-sky-400 hover:text-sky-600 font-medium text-slate-600">
                  {d.r}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
